import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { hasPermission } from 'src/app/utils/functions/permission.function';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { PermissionsService } from 'src/app/services/permissions.service';
import { from } from 'rxjs';
import { AllApiPermissions } from 'src/app/utils/interfaces/auth.interfaces';

export interface IPermission {
  /**
   * The permission type/key
   */
  type: any;
  /**
   * The required operation for this permission
   */
  operation: OperationEnum;
  /**
   * When set to true this permission is optional. A non-optional permission that is missing will always cause a routing fail.
   * An optional permission that is missing will not cause a routing fail as long as at least one optional permission is granted.
   */
  optional?: boolean;
}

/**
 * Interface that should be implemented by page components that require user permissions before they can be opened
 */
export interface IRequiresPermissions {
  /**
   * Function called by the AuthorizedGuard to determine which permissions need to be checked before opening a page
   * @param route The current route
   */
  agGetRequiredPermissions(route: ActivatedRouteSnapshot): IPermission[];
}

export const authorizedGuard: CanActivateFn = (r, s) => from(inject(AuthorizedGuard).canActivate(r, s));

@Injectable({
  providedIn: 'root',
})
export class AuthorizedGuard  {
  constructor(
    private store: Store<IRemoteXPACPortalState>,
    private permissionsService: PermissionsService,
    private router: Router
  ) { }

  public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // Do a permission check if the component has support for that
    const componentWithPermissions = route.routeConfig.component?.prototype as unknown as IRequiresPermissions;

    if (!componentWithPermissions?.agGetRequiredPermissions) {
      // Component does not implement interface
      return true;
    }

    const permissions = await this.permissionsService.getPermissions();

    // Check if we have all required permissions
    let hasOptionalPermissions: boolean = false;
    let hasAtLeastOneOptionalPermission: boolean = false;
    const requiredPermissions: IPermission[] = componentWithPermissions.agGetRequiredPermissions(route);
    for (let i = 0; i < requiredPermissions.length; i++) {
      const permission: IPermission = requiredPermissions[i];

      if (permission.optional) {
        hasOptionalPermissions = true;
      }

      const granted: boolean = hasPermission(permission.type, permissions, permission.operation);

      if (!granted && !permission.optional) {
        // Non-optional permission is missing, do not allow routing
        console.error(`Missing '${permission.operation}' permission for '${permission.type}'`);

        this.router.navigate(['dashboard']);

        return false;
      }
      else if (granted && permission.optional) {
        // We do have at least one optional permission
        hasAtLeastOneOptionalPermission = true;
      }
    }

    if (hasOptionalPermissions && !hasAtLeastOneOptionalPermission) {
      // We require optional permissions, but have none of them. Do not allow routing.
      console.error(`Missing all optional permissions`);

      this.router.navigate(['dashboard']);

      return false;
    }

    return true;
  }
}
